import { useState, useEffect } from "react";
import { getGcChecklist } from "../services/preconService";

interface ApiError {
  message?: string;
}

interface ChecklistItem {
  key?: string;
  type: string;
  value: string | null;
}

interface RawChecklistData {
  projectType?: string;
  type?: string;
  items?: ChecklistItem[];
}

export interface NormalizedChecklist {
  projectType: string;
  itemsByKey: Record<string, { type: string; value: string | null }>;
}

const normalizeChecklist = (data: RawChecklistData): NormalizedChecklist => {
  const projectType = data.projectType ?? data.type ?? "commercial";

  const itemsByKey: Record<string, { type: string; value: string | null }> = {};
  (data.items ?? []).forEach((item) => {
    if (item.key) itemsByKey[item.key] = { type: item.type, value: item.value };
  });

  return { projectType, itemsByKey };
};

const useGetGcChecklist = (projectId: string | undefined) => {
  const [checklist, setChecklist] = useState<NormalizedChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      setError("");

      const { data, error: apiError } = await getGcChecklist(projectId) as {
        data: { data?: RawChecklistData } | null;
        error: ApiError | null;
      };

      if (apiError) {
        setError(apiError.message || "Failed to load checklist.");
        setLoading(false);
        return;
      }

      setChecklist(normalizeChecklist((data?.data ?? data ?? {}) as RawChecklistData));
      setLoading(false);
    };

    fetch();
  }, [projectId]);

  return { checklist, loading, error };
};

export default useGetGcChecklist;
